import { useEffect, useMemo, useRef, useState } from "react"
import {
  ColumnDef,
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import classNames from "classnames"
import styled from "styled-components"

import { UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"

import { Spinner } from "components/common/Spinner"

import { getTableHeaderCellsConfig } from "./getTableHeaderCellsConfig"
import TableBody from "./TableBody"
import TableHeader from "./TableHeader"

import styles from "./index.module.scss"

export interface TableProps {
  tableName?: string
  headerCells: UITypes.TableHeaderCell[]
  data?: UITypes.TableBodyRow[]
  isLoading?: boolean
  currentItemID?: number | string | null
  currentItemIDs?: number[] | string[] | undefined
  statusOptions?: UITypes.Option[]
  className?: string
  isHeaderChecked?: boolean
  editable?: boolean
  columnsLoaded?: boolean
  isMobileDevice?: boolean
  rowsExpanded?: boolean
  fitContainer?: boolean
  onHeaderCheckboxChange?: () => void
  onCheckboxChange?: (id: number) => void
  onDeleteClick?: (id: number) => void
  onEditClick?: (id: number) => void
  onSort?: (data: UITypes.Sort) => void
  onRowClick?: (id: number) => void
  getRowActions?: (rowData: UITypes.TableBodyRow) => TableActionButtonGroup
}

const TableContainer = styled.div<{ width: number }>`
  width: ${props => props.width}px;
  font-size: 14px;
`

function Table({
  tableName,
  headerCells,
  data,
  isLoading,
  currentItemID,
  currentItemIDs,
  className,
  isHeaderChecked,
  columnsLoaded = true,
  isMobileDevice = false,
  fitContainer = false,
  onHeaderCheckboxChange,
  onCheckboxChange,
  onDeleteClick,
  onEditClick,
  onRowClick,
  getRowActions,
  rowsExpanded,
}: TableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>(rowsExpanded || {})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [widthsCalculated, setWidthsCalculated] = useState(!fitContainer)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasCalculatedRef = useRef(false)

  const hiddenColumns = useMemo(
    () => headerCells.filter(cell => cell.visibility === false).map(cell => cell.name),
    [headerCells],
  )

  const columns = useMemo<ColumnDef<UITypes.TableBodyRow, UITypes.TableBodyCell>[]>(
    () => getTableHeaderCellsConfig(headerCells),
    [headerCells],
  )

  const table = useReactTable<UITypes.TableBodyRow>({
    columns,
    data: data || [],
    getCoreRowModel: getCoreRowModel(),
    getSubRows: row => row.subRows,
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    enableSorting: false,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    onColumnSizingChange: setColumnSizing,
    defaultColumn: {
      maxSize: 500,
    },
    state: {
      expanded,
      columnSizing,
      columnVisibility: {
        ...hiddenColumns.reduce((acc, column) => ({ ...acc, [column]: false }), {}),
      },
    },
    meta: {
      currentItemID: currentItemID,
      currentItemIDs: currentItemIDs,
      isHeaderChecked: isHeaderChecked,
      onCheckboxChange: onCheckboxChange,
      onHeaderCheckboxChange: onHeaderCheckboxChange,
    },
  })

  useEffect(() => {
    if (!fitContainer || hasCalculatedRef.current) {
      return
    }

    if (!containerRef.current || columns.length === 0) {
      return
    }

    const containerWidth = containerRef.current.clientWidth
    if (!containerWidth) return

    const totalSize = table.getTotalSize()
    if (totalSize <= 0) {
      setWidthsCalculated(true)
      hasCalculatedRef.current = true
      return
    }

    if (totalSize < containerWidth) {
      const visibleColumns = table.getAllColumns().filter(column => column.getIsVisible())

      let miniTotalSize = 0
      let resizableTotalSize = 0

      visibleColumns.forEach(column => {
        const headerCell = headerCells.find(cell => cell.name === column.id)
        if (headerCell?.size === "mini") {
          miniTotalSize += column.getSize()
        } else {
          resizableTotalSize += column.getSize()
        }
      })

      const availableWidth = containerWidth - miniTotalSize

      if (availableWidth > 0 && resizableTotalSize > 0) {
        const scaleFactor = availableWidth / resizableTotalSize
        const newSizing: ColumnSizingState = {}

        visibleColumns.forEach(column => {
          const headerCell = headerCells.find(cell => cell.name === column.id)
          const isMini = headerCell?.size === "mini"

          if (!isMini) {
            const currentSize = column.getSize()
            const scaledSize = currentSize * scaleFactor
            newSizing[column.id] = scaledSize
          }
        })

        setColumnSizing(newSizing)
      }
    }

    hasCalculatedRef.current = true
    setWidthsCalculated(true)
  }, [fitContainer, data, columns.length, table, headerCells])

  return (
    <div ref={containerRef} className={classNames(styles.tableWrapper, className)}>
      {columnsLoaded && widthsCalculated && (
        <TableContainer width={table.getTotalSize()}>
          <TableHeader table={table} />
          <TableBody
            tableName={tableName}
            table={table}
            onRowClick={onRowClick}
            onDeleteClick={onDeleteClick}
            onEditClick={onEditClick}
            currentItemID={currentItemID}
            currentItemIDs={currentItemIDs}
            isMobileDevice={isMobileDevice}
            getRowActions={getRowActions}
          />
        </TableContainer>
      )}
      {(isLoading || !columnsLoaded || !widthsCalculated) && (
        <div className={styles.spinnerWrapper}>
          <Spinner />
        </div>
      )}
    </div>
  )
}

export default Table
